import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getMarketingConsent } from '@/lib/analytics/consent';
import { initMetaPixel, isPixelInitialised } from '@/lib/analytics/metaPixel';
import { initPinterestTag, isPinterestInitialised, trackPinterestPageView } from '@/lib/analytics/pinterest';
import { trackPageView } from '@/lib/analytics/events';

/**
 * Analytics provider that lives inside <BrowserRouter>.
 * - Initialises Meta Pixel + Pinterest Tag when marketing consent is granted.
 * - Fires PageView on every SPA route change.
 * - Re-checks consent on each navigation in case user just accepted.
 */
export function AnalyticsProvider() {
  const location = useLocation();
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    const consent = getMarketingConsent();
    if (consent !== 'granted') return;

    // Initialise Meta Pixel if not yet done (idempotent)
    const wasMetaInitialised = isPixelInitialised();
    if (!wasMetaInitialised) {
      initMetaPixel();
    }
    // Fire on first init (first load) or on genuine navigation — trackPageView() is a no-op if pixel not ready
    if (!wasMetaInitialised || prevPath.current !== location.pathname) {
      trackPageView();
    }

    // Initialise Pinterest Tag if not yet done (idempotent)
    const wasPinterestInitialised = isPinterestInitialised();
    if (!wasPinterestInitialised) {
      initPinterestTag();
    }
    if (!wasPinterestInitialised || prevPath.current !== location.pathname) {
      trackPinterestPageView();
    }

    prevPath.current = location.pathname;
  }, [location.pathname]);

  return null;
}
